import { createTheme, keyframes, alpha } from "@mui/material/styles";


export const colors = ({
  grey: {
    100: "#e0e0e0",
    200: "#c2c2c2",
    300: "#a3a3a3",
    400: "#858585",
    500: "#666666",
    600: "#525252",
    700: "#3d3d3d",
    800: "#292929",
    900: "#141414",
  },
  primary: {
    100: "#d0d1d5",
    200: "#a1a4ab",
    300: "#727681",
    400: "#1F2A40",
    500: "#141b2d",
    600: "#101624",
    700: "#0c101b",
    800: "#080b12",
    900: "#040509",
  },
  greenAccent: {
    100: "#dbf5ee",
    200: "#b7ebde",
    300: "#94e2cd",
    400: "#70d8bd",
    500: "#4cceac",
    600: "#3da58a",
    700: "#2e7c67",
    800: "#1e5245",
    900: "#0f2922",
  },
  redAccent: {
    100: "#f8dcdb",
    200: "#f1b9b7",
    300: "#e99592",
    400: "#e2726e",
    500: "#db4f4a",
    600: "#af3f3b",
    700: "#832f2c",
    800: "#58201e",
    900: "#2c100f",
  },
  blueAccent: {
    100: "#e1e2fe",
    200: "#c3c6fd",
    300: "#a4a9fc",
    400: "#868dfb",
    500: "#6870fa",
    600: "#535ac8",
    700: "#3e4396",
    800: "#2a2d64",
    900: "#151632",
  },
  lightBlue: {
    100: "#e3f8fa", // molto chiaro
    200: "#c3ecf1",
    300: "#a2dfe7",
    400: "#89d5df",
    500: "#73ccd7", // colore base
    600: "#5aa2aa",
    700: "#42787e",
    800: "#2a4e52",
    900: "#132526"  // molto scuro
  },
});


export const palette = {
  mode: "dark",
  text: {
    primary: "#eaeaea",
    disable: "#475569"
  },
  primary: {
    main: "#459095",
    dark: "#45909593",
    light: "#60adb3ff",
    contrastText: "#eaeaea",
  },
  secondary: {
    dark: "#E6371E",
    main: '#EB5C30',
    light: "#EF7745",
    contrastText: '#fff',
  },
  success: {
    main: "#34a327"
  },
  error: {
    main: '#c21c1cff',
    light: "#f25a5a",
    dark: '#831212ff',
    contrastText: '#fff',
  },
  background: {
    default: "#000",
    paper: "#1e1e1e",
    light: "#2e2e2e"
  }
}

const theme = createTheme({
  palette: palette,
  typography: {
    fontFamily: "Poppins",
    fontSize: 12,
    color: "#eaeaea",
    h1: { fontSize: 40, },
    h2: { fontSize: 32, },
    h3: { fontSize: 24, },
    h4: { fontSize: 20, },
    h5: { fontSize: 16, },
    h6: { fontSize: 14, },
  },
  components: {
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked .MuiSwitch-thumb": {
            backgroundColor: "#459095",
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#45909570",
            opacity: 1,
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e1e"
        },
      }
    },
    MuiDrawer: {
      styleOverrides: {
        root: { flexShrink: 0 },
        paper: {
          // Stesso colore pieno del topbar (.bg-topbar-dark in index.css), distinto dallo sfondo nero.
          backgroundColor: '#141414',
          backgroundImage: 'none',
          borderRight: `none`,
          transition: "width 0.3s ease"
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "5px",
          fontWeight: 400,
          padding: "6px 16px",
          backgroundColor: palette.background.light,
          color: "#eaeaea",
          "&:hover": {
            backgroundColor: "#eaeaea",
            color: "#000000"
          },
        },
        contained: {
          backgroundColor: "#459095",
          color: "#eaeaea",
          "&:hover": {
            backgroundColor: "#45909593",
            color: "#eaeaea"
          },
        },
        containedError: {
          backgroundColor: palette.error.main,
          color: "#eaeaea",
          "&:hover": {
            backgroundColor: palette.error.dark,
            color: "#eaeaea"
          }
        },
        outlined: {
          color: "#eaeaea",
          backgroundColor: "transparent",
          border: `0.5px solid ${"#459095"}`,
          "&:hover": {
            backgroundColor: alpha("#459095", 0.35),
            borderColor: palette.primary.light,
            color: "#eaeaea"
          },
        },
        outlinedError: {
          color: "#eaeaea",
          backgroundColor: "transparent",
          border: `0.5px solid ${palette.error.main}`,
          "&:hover": {
            backgroundColor: alpha(palette.error.main, 0.35),
            borderColor: palette.error.light,
            color: "#eaeaea"
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: palette.secondary.main
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.grey[700],
          backgroundImage: "none",
        },
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.grey[700],
          backgroundImage: "none",
        }
      }
    },
    MuiListItemText: {
      defaultProps: {
        slotProps: {
          primary: {
            noWrap: true,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          paddingLeft: '8px',
          paddingRight: '8px',
          '&:hover, &.Mui-selected, &.Mui-selected:hover': { color: '#459095' },
          justifyContent: "center",
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          backgroundColor: palette.background.light
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
          minWidth: "25px",
          justifyContent: 'center'
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          flexShrink: 0
        }
      }
    }
  }
});
export default theme;

export const logColor = {
  0: "darkorange",
  1: "#ce2528ff",
  2: "goldenrod",
  3: "#58a8ae",
  4: "palevioletred"
}

export const agentLogColor = {
  0: "darkorange",
  1: "#ce2528ff",
  2: "goldenrod",
  3: "#58a8ae",
  4: "palevioletred"
}

export const btnStyle = {
  fontWeight: 600,
  backgroundColor: "#2d2d2d",
  color: "#eaeaea",
  "&:hover": {
    backgroundColor: "#eaeaea !important",
    color: "#131C2D !important",
  }
}

export const boxStyle = {
  width: "100%",
  height: "100%",
  backgroundColor: "#1e1e1e",
  //border: "solid 1px #459095",
  borderRadius: "15px",
  color: "#fff",
  p: 2,
  position: 'relative'
}

export const animatedBoxStyle = {
  ...boxStyle,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
  }
}

export const styles = {
  container: {
    flex: 1,
    width: "100%",
    height: "100%",
    border: "solid 2px #459095",
    backgroundColor: " #1e1e1e",
    borderRadius: "15px",
    color: "#fff",
    p: 2
  },
  tiresiaContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
    border: "solid 2px #5E3F82",
    backgroundColor: " #1e1e1e",
    borderRadius: "15px",
    color: "#fff",
    p: 2
  },
  animatedContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
    border: "solid 2px #459095",
    backgroundColor: " #1e1e1e",
    borderRadius: "15px",
    color: "#fff",
    p: 2,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.050)",
      boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
    }
  },
  logColors: {
    0: "#FEA51D",
    1: "#931A1D",
    2: "#C9B85E",
    3: "#58a8ae",
    4: "#fff"
  },
  btn: {
    fontWeight: 900,
    backgroundColor: "#2d2d2d",
    color: "#eaeaea",
    "&:hover": {
      backgroundColor: "#eaeaea !important",
      color: "#131C2D !important",
    }
  }
};

export const dataGridStyle = {
  fontWeight: 400,
  height: "100%",
  width: "100%",
  borderRadius: "10px",
  border: "solid 1px #292929",
  overflowX: "auto",
  "& .MuiDataGrid-toolbar": {
    backgroundColor: "#292929",
  },
  "& .MuiToolbar-root": {
    gap: 2,
    backgroundColor: "#292929",
    borderBottom: "1px solid #1e1e1e"
  },
  "& .MuiToolbar-root .MuiButton-root": {
    color: "#eaeaea",
    backgroundColor:alpha(palette.primary.main, 0.15),
    border: `1px solid ${palette.primary.light}`,
    borderRadius: "7px"
  },
  "& .MuiToolbar-root .MuiButton-root:hover": {
    //borderBottom: "1px solid #eaeaea",
    backgroundColor: palette.primary.main
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "none",
    backgroundColor: "#292929",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#292929",
    color: palette.primary.light
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #252525',
  },
  "& .MuiDataGrid-virtualScroller": {
    backgroundColor: "#1e1e1e",
  },
  '& .MuiDataGrid-row': {
    padding: 0,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
    borderBottomColor: 'transparent',
  },
};

const colorChange = keyframes`
  0% {
    color: #43B4B9;
  }
  33% {
    color: #596035;
  }
  66% {
    color: #562984;
  }
  100% {
    color: #43B4B9; 
  }
`;

export const dataGridTiresia = {
  marginTop: 4,
  fontWeight: 900,
  height: "30vh",
  width: "100%",
  borderRadius: 5,
  border: "solid 3px #459095",
  animation: `${colorChange} 3s linear infinite`,
  overflowX: "auto",
  "& .MuiDataGrid-root": {
    border: "none",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "none",
    py: 2
  },
  "& .name-column--cell": {
    color: "#1D7B91",
    fontWeight: 900,
  },
  "& .MuiDataGrid-container--top [role=row]": {
    backgroundColor: "#5E3F82 !important",
  },
  "& .MuiDataGrid-toolbarContainer": {
    borderTopRightRadius: 17,
    borderTopLeftRadius: 17,
    gap: 4,
    backgroundColor: "#5e3f82",
    color: "#1D7B91",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "none",
    backgroundColor: "#5e3f82",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  "& .MuiCheckbox-root": {
    color: "#1D7B91 !important",
  },
  "& .MuiButtonBase-root": {
    color: "#fff !important",
  },
  "& .MuiCircularProgress-root": {
    color: "#5b9ba9 !important",
  },
  "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell":
  {
    whiteSpace: "normal !important",
  },
  "& .MuiBadge-badge": {
    backgroundColor: "#dd8d16",
  },
  "& .MuiDataGrid-root .MuiDataGrid-container--bottom [role=row]": {
    background: "#D0D0D0 !important",
  },
  "& .MuiDataGrid-panelWrapper": {
    width: "600px !important",
  },
  "& .MuiDataGrid-overlayWrapperInner": {
    backgroundColor: "#1a1a1a !important",
  },
  "& .MuiDataGrid-virtualScrollerContent": {
    backgroundColor: "#1a1a1a !important",
  },
  "& .MuiDataGrid-filler": {
    backgroundColor: "#1a1a1a !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 900,
  },
  "& .MuiDataGrid-virtualScroller": {
    borderRadius: 0,
  },
}